import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import axios from 'axios';
import { Eye, EyeOff, Loader2, Shield } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_track-bills-app/artifacts/0n1q2nvp_My%20Direct%20debit%20app%20logo.jpg";

export default function AdminLogin() {
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { refreshUser } = useAuth();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const { data } = await axios.post(
        `${API_URL}/api/admin/login`,
        { email: formData.email, password: formData.password },
        
      );

      // Store token for cross-origin auth
      if (data.access_token) {
        localStorage.setItem('mdd_access_token', data.access_token);
      }

      if (data.is_admin) {
        await refreshUser();
        toast.success('Welcome, Admin!');
        navigate('/admin');
      }
    } catch (err) {
      const message = err.response?.data?.detail || 'Invalid admin credentials';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-dark flex flex-col items-center justify-center px-4">
      {/* Logo */}
      <div className="mb-8 text-center">
        <img src={LOGO_URL} alt="My Direct Debits" className="h-16 w-16 mx-auto mb-4 rounded-full" />
        <h1 className="font-heading font-bold text-white text-2xl">Admin Portal</h1>
        <p className="text-white/60 text-sm mt-1">My Direct Debits</p>
      </div>

      {/* Login Card */}
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          <div className="flex items-center justify-center gap-2 mb-6">
            <div className="w-10 h-10 bg-primary-light rounded-full flex items-center justify-center">
              <Shield className="w-5 h-5 text-primary" />
            </div>
            <h2 className="font-heading font-bold text-dark text-xl">
              Founder Access
            </h2>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Admin Email
              </label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="Enter admin email"
                data-testid="admin-email-input"
                className="input-field w-full"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Admin Password
              </label>
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="Enter admin password"
                  data-testid="admin-password-input"
                  className="input-field w-full pr-12"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-text-light hover:text-text-secondary"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {error && (
              <div className="bg-alert-light text-alert text-sm p-3 rounded-lg" data-testid="admin-error">
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              data-testid="admin-login-btn"
              className="btn-primary w-full py-3 text-base"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin mx-auto" />
              ) : (
                'Access Admin Portal'
              )}
            </Button>
          </form>

          <p className="text-center text-text-light text-xs mt-6">
            Restricted access for authorized personnel only
          </p>
        </div>
      </div>

      {/* Footer */}
      <p className="text-white/40 text-xs mt-8">
        © 2026 Voliciti Group Ventures Ltd
      </p>
    </div>
  );
}
